/**
 * Level 2 of the hedge hierarchy: one account pair's symbol hedges.
 *
 * Layout (header-driven, matches the approved plan):
 *
 *   Maven ↔ Blueberry                 [+ Add Hedge]  [⋯ More]
 *   cross firm hedge · active · quality: perfect
 *
 *   [legs header: firms, logins, phase]
 *
 *   Allowlist: [EURUSD ×] [GBPUSD ×] [+ Add Symbol]   (collapsible)
 *
 *   Active Hedges  (primary, scrolls)
 *     [ EURUSD card ] [ GBPUSD card ] ...
 *
 * `[+ Add Hedge]` opens a right-side Sheet with symbol validation.
 * `[⋯ More]` dropdown exposes Pause / Replace Leg / Dissolve.
 */

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { Loader2, Plus, MoreHorizontal, Play } from "@/components/ui/icons";
import { DashboardSection } from "@/components/dashboard-section";
import { FirmLogo } from "@/components/firm-logo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddHedgeSheet } from "@/components/add-hedge-sheet";
import { extractApiErrorMessage } from "@/lib/api";
import { useConfirm, usePrompt } from "@/components/ui/dialog-imperative";
import {
  activateAccountPair,
  archiveAccountPair,
  getAccountPair,
  reopenAccountPair,
  setAccountPairAiMode,
  swapAccountPairLeg,
  updateAllowlist,
  type AccountPairDetailRes,
} from "@/lib/api/account-pairs";
import { dissolveAccountPair } from "@/lib/api/pair-proposals";
import { cn } from "@/lib/utils";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";
import { usePresencePinger } from "@/hooks/use-presence-pinger";
import { useDashboardStore } from "@/store/dashboard.store";
import { PairSleepWakeToggle } from "@/components/pair-sleep-wake-toggle";
import { PairModeToggle } from "@/components/pair-mode-toggle";
import { PairOverrideToggle } from "@/components/pair-override-toggle";
import { PairAiPauseButton } from "@/components/pair-ai-pause-button";
import { PairSuggestionBanner } from "@/components/pair-suggestion-banner";
import { activatePair, setPairModeOverride } from "@/lib/api/pairs";
import { toast } from "sonner";
import { AsleepSnapshotBadge } from "@/components/asleep-snapshot-badge";
import { LiveCell, LiveDot } from "@/components/live-cell";
import { fmtMoney } from "@/lib/format";
import { TradingGrowthCurve } from "@/components/trading-growth-curve";
import { TradingObjectives } from "@/components/trading-objectives";
import { WithdrawalRoadmap } from "@/components/withdrawal-roadmap";
import { AccountLivePositions } from "@/components/account-live-positions";
import { AccountTradesHistory } from "@/components/account-trades-history";

const PREFERRED_SYMBOLS = [
  "XAUUSD",
  "XAGUSD",
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
] as const;

function defaultLotsForAccountSize(accountSize: number | null | undefined): number {
  if (typeof accountSize !== "number" || accountSize <= 0) return 0.01;
  return Math.max(0.01, Math.min(0.10, Math.floor((accountSize / 100000) * 100) / 100));
}

function accountPhaseLabel(
  longPhase: "step1" | "step2" | "funded",
  shortPhase: "step1" | "step2" | "funded",
): string {
  if (longPhase === shortPhase) {
    if (longPhase === "step1") return "Step 1";
    if (longPhase === "step2") return "Step 2";
    return "Funded";
  }
  return "Mixed";
}

function LegsHeader({
  pair,
}: {
  pair: AccountPairDetailRes["accountPair"];
}): ReactNode {
  return (
    <DashboardSection>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <FirmLogo
            src={pair.longAccount.firmLogo}
            name={pair.longAccount.firmName}
            className="h-12 w-12 shrink-0 rounded-lg border border-border object-contain p-1.5"
          />
          <div className="min-w-0">
            <p className="truncate font-sans text-sm font-medium text-foreground">
              {pair.longAccount.firmName}
            </p>
            <p className="truncate font-sans text-xs text-muted-foreground">
              {pair.longAccount.login ? `#${pair.longAccount.login}` : "—"} ·{" "}
              {pair.longAccount.platformType} · {pair.longAccount.phase}
            </p>
          </div>
        </div>

        <span
          className="ci-Arrow_Left_Right self-center text-2xl text-muted-foreground sm:self-auto"
          aria-hidden
        />

        <div className="flex min-w-0 items-center gap-4 sm:flex-row-reverse">
          <FirmLogo
            src={pair.shortAccount.firmLogo}
            name={pair.shortAccount.firmName}
            className="h-12 w-12 shrink-0 rounded-lg border border-border object-contain p-1.5"
          />
          <div className="min-w-0 sm:text-right">
            <p className="truncate font-sans text-sm font-medium text-foreground">
              {pair.shortAccount.firmName}
            </p>
            <p className="truncate font-sans text-xs text-muted-foreground">
              {pair.shortAccount.login ? `#${pair.shortAccount.login}` : "—"} ·{" "}
              {pair.shortAccount.platformType} · {pair.shortAccount.phase}
            </p>
          </div>
        </div>
      </div>
    </DashboardSection>
  );
}

function AllowlistBlock({
  slug,
  allowlist,
  preferredSymbols,
  onChange,
}: {
  slug: string;
  allowlist: string[];
  preferredSymbols: readonly string[];
  onChange: (next: string[]) => void;
}): ReactNode {
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(allowlist.length >= 6);

  const commit = async (list: string[]): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const saved = await updateAllowlist(slug, list);
      onChange(saved);
      setNext("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const add = (): void => {
    const sym = next.trim().toUpperCase();
    if (!sym || allowlist.includes(sym)) return;
    void commit([...allowlist, sym]);
  };
  const remove = (sym: string): void => {
    void commit(allowlist.filter((s) => s !== sym));
  };

  return (
    <DashboardSection>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-sans text-sm font-medium text-foreground">
              Symbol Allowlist
            </h2>
            <p className="font-sans text-xs text-muted-foreground">
              Haiku may only open trades on these symbols.{" "}
              {allowlist.length === 0 && "Add a symbol before the AI can trade."}{" "}
              Start with <span className="font-mono">XAUUSD</span> if you want the default gold setup.
            </p>
          </div>
          {allowlist.length >= 4 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCollapsed((c) => !c)}
              className="text-xs"
            >
              {collapsed ? "Show All" : "Collapse"}
            </Button>
          )}
        </div>

        {!collapsed && (
          <div className="flex flex-wrap gap-2">
            {allowlist.length === 0 ? (
              <p className="font-sans text-xs text-muted-foreground">
                No symbols authorized yet.
              </p>
            ) : (
              allowlist.map((sym) => (
                <Badge
                  key={sym}
                  variant="outline"
                  className="flex items-center gap-1 text-xs"
                >
                  <span className="font-mono">{sym}</span>
                  <button
                    type="button"
                    onClick={() => remove(sym)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${sym}`}
                    disabled={saving}
                  >
                    ×
                  </button>
                </Badge>
              ))
            )}
          </div>
        )}

        {collapsed && (
          <p className="text-xs text-muted-foreground">
            {allowlist.length} symbols on allowlist — expand to edit.
          </p>
        )}

        <div className="flex items-center gap-2">
          <Input
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="XAUUSD"
            autoComplete="off"
            spellCheck={false}
            aria-label="Symbol to add to allowlist"
            className="max-w-[12rem] font-mono text-xs uppercase"
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            disabled={saving}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={add}
            disabled={saving || !next.trim()}
            aria-busy={saving}
          >
            {saving && <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />}
            {saving ? "Saving…" : "Add"}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {preferredSymbols
            .filter((sym) => !allowlist.includes(sym))
            .map((sym) => (
              <Button
                key={sym}
                size="sm"
                variant="outline"
                className="h-7 px-2 font-mono text-[11px]"
                onClick={() => {
                  setNext(sym);
                  void commit([...allowlist, sym]);
                }}
                disabled={saving}
              >
                {sym}
              </Button>
            ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Prefer the <span className="font-medium">Add Hedge</span> button above —
          it validates the symbol on both brokers before adding.
          Index symbols are broker-specific, so use the broker's own
          symbol when you add them.
        </p>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </DashboardSection>
  );
}

/**
 * Hidden child that opens one WS subscription per symbol-pair id.
 * React's rules-of-hooks forbid calling useHedgeSocket in a loop from
 * a parent, so we mount one of these per pair and let each call the
 * hook once. The store is keyed by pairId so events stay namespaced.
 */
function PairSubscriber({ pairId }: { pairId: string }): ReactNode {
  useHedgeSocket(pairId);
  return null;
}

/**
 * Aggregated per-leg summary block that sits between LegsHeader and
 * AllowlistBlock. Sums live openPnl across every symbol pair bound
 * to the account and picks equity/balance from the most-recently
 * updated snapshot so the operator sees account-wide numbers.
 */
function LegsLive({
  accountPair,
  symbolPairs,
}: {
  accountPair: AccountPairDetailRes["accountPair"];
  symbolPairs: AccountPairDetailRes["symbolPairs"];
}): ReactNode {
  const pairBuckets = useDashboardStore((s) => s.pairs);
  const symbolPairIds = symbolPairs.map((sp) => sp.id);

  const aggregate = (accountId: string) => {
    let equity: number | null = null;
    let balance: number | null = null;
    let updatedAt = 0;
    let anyLive = false;
    let online: boolean | null = null;
    // Equity / balance / online come from the freshest account tick
    // across the symbol-pair slices — the broker footer reports one
    // value per account, fanned out to every pair the account belongs
    // to, so picking the freshest avoids the N× bleed bug.
    for (const pid of symbolPairIds) {
      const acc = pairBuckets[pid]?.accounts?.[accountId];
      if (!acc) continue;
      anyLive = true;
      if (acc.updatedAt > updatedAt) {
        updatedAt = acc.updatedAt;
        equity = acc.equity;
        balance = acc.balance;
        online = acc.online;
      }
    }
    // Open P&L = sum of every open position the dashboard knows about
    // on this account, across every symbol-pair slice. Same source of
    // truth the live-positions list and per-symbol pair card already
    // use — that's why those numbers tick smoothly while the broker-
    // footer-derived `accountAggregateOpenPnl` field flickered for
    // Match-Trader (its Profit header cell re-rendered to empty mid-
    // digest, the driver fell back to 0, and the store overwrote the
    // field with the fake 0). Summing positions sidesteps that path.
    let openPnl: number | null = null;
    let anyPosition = false;
    for (const pid of symbolPairIds) {
      const positions = pairBuckets[pid]?.positions;
      if (!positions) continue;
      for (const pos of Object.values(positions)) {
        if (pos.accountId !== accountId) continue;
        if (!anyPosition) {
          openPnl = 0;
          anyPosition = true;
        }
        openPnl = (openPnl ?? 0) + pos.openPnl;
      }
    }
    return { equity, balance, openPnl, updatedAt, anyLive, online };
  };

  // Overall connection state — "open" if any subscription is live.
  const connectionStatus: "connecting" | "open" | "closed" = (() => {
    let seenConnecting = false;
    for (const pid of symbolPairIds) {
      const s = pairBuckets[pid]?.connectionStatus;
      if (s === "open") return "open";
      if (s === "connecting") seenConnecting = true;
    }
    return seenConnecting ? "connecting" : "closed";
  })();

  const sides = [
    { side: "long" as const, acc: accountPair.longAccount },
    { side: "short" as const, acc: accountPair.shortAccount },
  ];

  // Reference floors for the Trading Growth Curve are derived from
  // the persistent rule percentages on the account row — `accountSize`
  // is the anchor; max-loss / daily-loss / profit-target percentages
  // (already enriched onto AccountPairSide by the worker handler) get
  // multiplied through. Returns nulls when the operator hasn't
  // captured the firm's rule numbers yet, in which case the chart
  // hides the corresponding reference line.
  const referenceFloors = (acc: AccountPairDetailRes["accountPair"]["longAccount"]) => {
    const start = acc.accountSize ?? null;
    const maxLossPct = acc.maxDrawdownPct ?? null;
    const dailyLossPct = acc.dailyLossPct ?? null;
    const profitTargetPct = acc.profitTargetPct ?? null;
    return {
      startBalance: start,
      maxLossFloorUsd:
        start != null && maxLossPct != null ? start - start * (maxLossPct / 100) : null,
      dailyLossFloorUsd:
        start != null && dailyLossPct != null
          ? start - start * (dailyLossPct / 100)
          : null,
      profitTargetUsd:
        start != null && profitTargetPct != null
          ? start + start * (profitTargetPct / 100)
          : null,
    };
  };

  return (
    <DashboardSection>
      <div className="flex items-center justify-between">
        <h2 className="font-sans text-sm font-medium text-foreground">
          Live Account Stats
        </h2>
        <LiveDot status={connectionStatus} />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {sides.map(({ side, acc }) => {
          const live = aggregate(acc.accountId);
          const isLive = live.anyLive;
          const equityDisplay = live.equity != null ? fmtMoney(live.equity) : "—";
          const balanceDisplay = live.balance != null ? fmtMoney(live.balance) : "—";
          const pnlDisplay = live.openPnl == null ? "—" : fmtMoney(live.openPnl);
          const pnlColor =
            live.openPnl == null
              ? undefined
              : live.openPnl >= 0
                ? "text-emerald-500"
                : "text-destructive";
          const floors = referenceFloors(acc);
          return (
            <div key={side} className="flex min-w-0 flex-col gap-3">
              <div className="rounded-lg border border-border/50 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-2">
                  <Badge variant="outline" className="truncate text-[10px]">
                    {acc.firmName}
                  </Badge>
                  {live.online != null && (
                    <span
                      className={cn(
                        "h-2 w-2 rounded-full",
                        live.online ? "bg-emerald-500" : "bg-muted-foreground/30",
                      )}
                      title={live.online ? "Online" : "Offline"}
                    />
                  )}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <LiveCell label="Equity" value={equityDisplay} live={isLive} />
                  <LiveCell label="Balance" value={balanceDisplay} live={isLive} />
                  <LiveCell
                    label="Open P&L"
                    value={pnlDisplay}
                    color={pnlColor}
                    live={isLive}
                  />
                </div>
                {live.updatedAt > 0 && (
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground/80">
                    Updated {new Date(live.updatedAt).toLocaleTimeString()}
                  </p>
                )}
                {(() => {
                  // Asleep iff every symbol-pair slice for this account
                  // reports the leg as asleep/orphan/unknown — if ANY is
                  // awake, the account-level Brave is up. Empty buckets
                  // mean "no live event yet" → fall back to the snapshot
                  // we got back from REST.
                  let anyAwake = false;
                  let anyAsleepy = false;
                  for (const pid of symbolPairIds) {
                    const legState = pairBuckets[pid]?.sleep?.legStates?.[acc.accountId];
                    if (legState === "awake") anyAwake = true;
                    if (legState === "asleep" || legState === "orphan") anyAsleepy = true;
                  }
                  const isAsleep =
                    !anyAwake &&
                    (anyAsleepy ||
                      // Fall back to snapshot freshness as a "we slept
                      // recently and never came back" signal on first paint.
                      (acc.lastSleepSnapshotAt != null && !isLive));
                  return (
                    <AsleepSnapshotBadge
                      isAsleep={isAsleep}
                      snapshot={acc.lastSleepSnapshot ?? null}
                      snapshotAt={acc.lastSleepSnapshotAt ?? null}
                      className="mt-2"
                    />
                  );
                })()}
              </div>

              <TradingGrowthCurve
                accountId={acc.slug ?? acc.accountId}
                startBalance={floors.startBalance}
                anchorDateMs={accountPair.createdAt}
                maxLossFloorUsd={floors.maxLossFloorUsd}
                dailyLossFloorUsd={floors.dailyLossFloorUsd}
                profitTargetUsd={floors.profitTargetUsd}
                liveBalance={live.balance}
                liveEquity={live.equity}
              />

              <TradingObjectives
                accountId={acc.slug ?? acc.accountId}
                liveEquity={live.equity}
              />

              <WithdrawalRoadmap
                accountId={acc.slug ?? acc.accountId}
                liveEquity={live.equity}
              />

              {/* Live positions ledger — what the dashboard CURRENTLY
                  thinks is open on this leg. Aggregates positions from
                  every symbol-pair slice in the store filtered by
                  accountId. Surfaces duplicate (symbol+side) tickets
                  and stale rows so the operator can spot orphans
                  without diffing the broker terminal manually. */}
              <AccountLivePositions
                accountId={acc.accountId}
                symbolPairIds={symbolPairIds}
              />

              {/* Trades ledger — pulls trades.* from D1 so the
                  operator can audit every fill on this leg without
                  leaving the pair page. Open rows overlay live PnL
                  from the WS slice (rawAccountId + symbolPairIds)
                  because trades.pnl stays null until close. Live
                  positions card above stays as the realtime view;
                  this is the permanent record. */}
              <AccountTradesHistory
                accountId={acc.slug ?? acc.accountId}
                rawAccountId={acc.accountId}
                symbolPairIds={symbolPairIds}
              />
            </div>
          );
        })}
      </div>
    </DashboardSection>
  );
}

function ActiveHedges({
  accountPair,
  symbolPairs,
  onChanged,
}: {
  accountPair: AccountPairDetailRes["accountPair"];
  symbolPairs: AccountPairDetailRes["symbolPairs"];
  onChanged?: () => void;
}): ReactNode {
  if (symbolPairs.length === 0) {
    return (
      <DashboardSection>
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <p className="mb-1 text-sm font-medium text-foreground">
            No Symbol Hedges Yet
          </p>
          <p className="max-w-sm text-xs text-muted-foreground">
            Click the Add Hedge button in the header to open the validation
            + creation flow. New pairs land in setup so you can pick the
            AI mode (Manual / Semi / Auto) before activation.
          </p>
        </div>
      </DashboardSection>
    );
  }
  return (
    <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
      {symbolPairs.map((sp, index) => (
        <HedgeCardLink
          key={sp.id}
          accountPair={accountPair}
          sp={sp}
          index={index}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

/**
 * One symbol-hedge tile. Reads live openPnl for both legs out of the
 * store (already populated because AccountPairDetailPage mounts a
 * PairSubscriber for this pairId). Shows each leg's live PnL side by
 * side — in a hedge the sum is ~0 by design, so the operator wants to
 * see which side is winning today, not a near-zero net.
 */
function HedgeCardLink({
  accountPair,
  sp,
  index = 0,
  onChanged,
}: {
  accountPair: AccountPairDetailRes["accountPair"];
  sp: AccountPairDetailRes["symbolPairs"][number];
  index?: number;
  onChanged?: () => void;
}): ReactNode {
  const reduced = useReducedMotion();
  const livePair = useDashboardStore((s) => s.pairs[sp.id]);
  // Per-leg P&L for THIS symbol pair = sum of positions in this pair's
  // slice belonging to the leg's account. Using livePair.accounts[].
  // accountAggregateOpenPnl here would bleed in P&L from every other
  // symbol pair the same account is hedging on.
  const livePositions = livePair?.positions;
  const status = livePair?.connectionStatus ?? "closed";
  const [activating, setActivating] = useState(false);
  let longPnl: number | null = null;
  let shortPnl: number | null = null;
  if (livePositions) {
    longPnl = 0;
    shortPnl = 0;
    for (const p of Object.values(livePositions)) {
      if (p.accountId === accountPair.longAccount.accountId) longPnl += p.openPnl;
      else if (p.accountId === accountPair.shortAccount.accountId) shortPnl += p.openPnl;
    }
  }

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduced
          ? { duration: 0 }
          : { delay: index * 0.04, duration: 0.22, ease: [0.22, 1, 0.36, 1] }
      }
      className="rounded-xl border border-border p-4 transition-colors hover:border-accent/40 hover:bg-muted/30"
    >
      <div className="flex items-center justify-between">
        <Link
          to={`/pairs/${encodeURIComponent(accountPair.slug)}/${encodeURIComponent(sp.symbol)}`}
          className="font-sans text-lg font-medium text-foreground hover:text-accent"
        >
          {sp.symbol}
        </Link>
        <div className="flex items-center gap-2">
          <LiveDot status={status} showLabel={false} />
          <Badge variant="outline" className="text-[10px] capitalize">
            {sp.status}
          </Badge>
        </div>
      </div>

      {/* Per-symbol-pair AI mode controls. Sits between the header and
          the metrics so the operator's eye lands on it before they
          decide whether to open / close. */}
      <div className="mt-3 flex items-center justify-between gap-2 rounded-md bg-muted/30 px-2 py-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          AI Mode
        </span>
        {sp.status === "setup" ? (
          <button
            type="button"
            disabled={activating}
            aria-busy={activating}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setActivating(true);
              try {
                await activatePair(sp.slug, "manual");
                toast.success(`${sp.symbol} activated in manual mode`);
                onChanged?.();
              } catch (err) {
                toast.error(err instanceof Error ? err.message : String(err));
              } finally {
                setActivating(false);
              }
            }}
            style={{ touchAction: "manipulation" }}
            className="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-white transition-colors hover:bg-accent/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/60 disabled:opacity-60"
          >
            {activating ? (
              <Loader2 aria-hidden="true" className="h-3 w-3 animate-spin" />
            ) : (
              <Play aria-hidden="true" className="h-3 w-3" />
            )}
            {activating ? "Activating…" : "Activate"}
          </button>
        ) : sp.status === "active" ? (
          <PairOverrideToggle
            override={sp.modeOverride}
            parentMode={accountPair.aiMode}
            status={sp.status}
            size="sm"
            onChange={async (next) => {
              try {
                await setPairModeOverride(sp.slug, next);
                onChanged?.();
              } catch (err) {
                toast.error(err instanceof Error ? err.message : String(err));
              }
            }}
          />
        ) : (
          <span className="text-[10px] text-muted-foreground">Closed</span>
        )}
      </div>

      {/* Surface pending Haiku suggestions for semi_auto pairs inline so
          the operator doesn't have to drill in to approve / reject. */}
      {sp.status === "active" && sp.mode === "semi_auto" && (
        <div className="mt-2">
          <PairSuggestionBanner pairSlug={sp.slug} />
        </div>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Lots</span>
        <span className="font-mono text-xs text-foreground">{sp.lots}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Account Phase</span>
        <span className="text-xs text-foreground">
          {accountPhaseLabel(accountPair.longAccount.phase, accountPair.shortAccount.phase)}
        </span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/40 pt-2">
        <span
          className="truncate text-xs text-muted-foreground"
          title={accountPair.longAccount.firmName}
        >
          {accountPair.longAccount.firmName}
        </span>
        {longPnl == null ? (
          <span className="font-mono text-xs text-muted-foreground/70">—</span>
        ) : (
          <span
            className={cn(
              "font-mono text-xs font-semibold tabular-nums",
              longPnl >= 0 ? "text-emerald-500" : "text-destructive",
            )}
          >
            {fmtMoney(longPnl)}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span
          className="truncate text-xs text-muted-foreground"
          title={accountPair.shortAccount.firmName}
        >
          {accountPair.shortAccount.firmName}
        </span>
        {shortPnl == null ? (
          <span className="font-mono text-xs text-muted-foreground/70">—</span>
        ) : (
          <span
            className={cn(
              "font-mono text-xs font-semibold tabular-nums",
              shortPnl >= 0 ? "text-emerald-500" : "text-destructive",
            )}
          >
            {fmtMoney(shortPnl)}
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function AccountPairDetailPage(): ReactNode {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [data, setData] = useState<AccountPairDetailRes | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Keep this account_pair's bots awake while the operator is on the page.
  usePresencePinger(slug ? [slug] : []);

  const load = (): void => {
    if (!slug) return;
    getAccountPair(slug)
      .then(setData)
      .catch((e) =>
        setLoadError(e instanceof Error ? e.message : String(e)),
      );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 font-sans">
        <p role="alert" aria-live="polite" className="text-sm text-destructive">
          Couldn’t load account pair: {loadError}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setLoadError(null);
            load();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (!data) {
    return (
      <div
        aria-busy="true"
        aria-label="Loading account pair"
        className="flex flex-col gap-4 font-sans"
      >
        <DashboardSection>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-48" />
              <div className="flex gap-2">
                <Skeleton className="h-7 w-24 rounded-md" />
                <Skeleton className="h-7 w-7 rounded-md" />
              </div>
            </div>
            <Skeleton className="h-3 w-72" />
          </div>
        </DashboardSection>
        <DashboardSection>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-6 w-16 rounded-md" />
              ))}
            </div>
          </div>
        </DashboardSection>
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex h-40 flex-col gap-3 rounded-xl border border-border p-4"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-12 rounded-md" />
              </div>
              <Skeleton className="h-8 w-full rounded-md" />
              <div className="mt-auto flex items-center justify-between">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-14" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-14" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const { accountPair, symbolPairs } = data;
  const minAccountSize = accountPair.longAccount.accountSize != null &&
      accountPair.shortAccount.accountSize != null
    ? Math.min(accountPair.longAccount.accountSize, accountPair.shortAccount.accountSize)
    : (accountPair.longAccount.accountSize ?? accountPair.shortAccount.accountSize ?? null);
  const defaultLots = defaultLotsForAccountSize(minAccountSize);
  const isPaused = accountPair.status === "paused";
  const isArchived =
    accountPair.status === "closed" && accountPair.archived === true;
  const qualityBadgeVariant =
    (accountPair as unknown as { quality?: string }).quality === "operator_forced"
      ? "warning"
      : (accountPair as unknown as { quality?: string }).quality === "near" ||
          (accountPair as unknown as { quality?: string }).quality === "mismatch"
        ? "warning"
        : "active";
  const quality = (accountPair as unknown as { quality?: string }).quality ?? "perfect";

  const doReopen = async (): Promise<void> => {
    setBusy(true);
    try {
      await reopenAccountPair(accountPair.slug);
      load();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doCompleteSetup = async (): Promise<void> => {
    const ok = await confirm({
      title: "Mark Setup Complete?",
      description:
        "Start trading on this pair. The AI heartbeat will resume and any symbol hedges still in setup will be activated.",
      confirmText: "Complete Setup",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await activateAccountPair(accountPair.slug);
      toast.success("Setup complete — pair is now active");
      load();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doDissolve = async (): Promise<void> => {
    const ok = await confirm({
      title: "Dissolve This Pair?",
      description:
        "Both accounts will be released back into the unpaired pool and any active hedges will be closed.",
      confirmText: "Dissolve Pair",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await dissolveAccountPair(accountPair.slug);
      navigate("/");
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Rebuild = same underlying dissolve-+-rematch, but routes the
   *  operator directly to the new pair (if one was found) so the
   *  "fix a bad pair" flow is one click instead of two pages. */
  const doRebuild = async (): Promise<void> => {
    const ok = await confirm({
      title: "Rebuild This Pair?",
      description:
        "Both legs will be released and immediately re-matched against the current pool. If either lands a better partner, you'll be taken to that new pair.",
      confirmText: "Rebuild",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await dissolveAccountPair(accountPair.slug);
      const nextSlug =
        result.long?.accountPairSlug ??
        result.short?.accountPairSlug ??
        null;
      if (nextSlug) navigate(`/pairs/${encodeURIComponent(nextSlug)}`);
      else navigate("/pairs");
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doSwapLeg = async (side: "long" | "short"): Promise<void> => {
    const current =
      side === "long" ? accountPair.longAccount : accountPair.shortAccount;
    const replacement = await prompt({
      title: side === "long" ? "Swap LONG Leg" : "Swap SHORT Leg",
      description: `Replacement for ${current.firmName} / #${current.login ?? current.slug}.`,
      label: "Account slug or id",
      placeholder: "e.g. a1b2c3d4e5f6",
      confirmText: "Swap Leg",
    });
    const newAccountId = replacement?.trim();
    if (!newAccountId) return;
    setBusy(true);
    try {
      await swapAccountPairLeg(accountPair.slug, {
        side,
        newAccountId,
        acknowledgedDeltas: false,
        reason: "instant replacement",
      });
      load();
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 font-sans">
      {/* Header row: title left, actions right */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-xl font-medium text-foreground break-words sm:text-2xl">
            <span className="break-words">{accountPair.longAccount.firmName}</span>{" "}
            <span className="text-muted-foreground">↔</span>{" "}
            <span className="break-words">{accountPair.shortAccount.firmName}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground capitalize">
            <span>{accountPair.firmPairMode} Firm Hedge</span>
            <span>·</span>
            <span>{accountPair.status}</span>
            <span>·</span>
            <Badge variant={qualityBadgeVariant} className={cn("text-[10px] capitalize")}>
              {quality.replace(/_/g, " ")}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isArchived && accountPair.status !== "closed" && (
            <>
              <PairModeToggle
                mode={accountPair.aiMode}
                status="active"
                size="sm"
                onChange={async (next) => {
                  try {
                    await setAccountPairAiMode(accountPair.slug, next);
                    load();
                  } catch (e) {
                    toast.error(extractApiErrorMessage(e));
                  }
                }}
              />
              <PairAiPauseButton
                accountPairSlug={accountPair.slug}
                paused={accountPair.aiPaused}
                aiMode={accountPair.aiMode}
                size="sm"
                onChanged={() => load()}
              />
              {/* Divider — separates AI intent (mode + pause) from
                  lifecycle controls (sleep/wake, add-hedge). Operators
                  asked for visual grouping so the AI cluster reads as
                  one concept. */}
              <span aria-hidden className="mx-0.5 h-5 w-px bg-border/60" />
            </>
          )}
          {accountPair.sleep && !isArchived && (
            <>
              <PairSleepWakeToggle
                accountPairSlug={accountPair.slug}
                state={{
                  mode: accountPair.sleep.mode,
                  manualState: accountPair.sleep.manualState,
                  currentState: accountPair.sleep.currentState,
                }}
                size="sm"
              />
              {/* Divider before the destructive / structural cluster
                  (Add Hedge, kebab menu) — same grouping principle as
                  the divider above. */}
              <span aria-hidden className="mx-0.5 h-5 w-px bg-border/60" />
            </>
          )}
          {isArchived ? (
            // Archived pair — only [Reopen] is offered. Add-hedge + dropdown
            // actions are meaningless while the pair is dormant.
            <Button size="sm" onClick={() => void doReopen()} disabled={busy}>
              Reopen Pair
            </Button>
          ) : (
            <>
              {isPaused && (
                <Badge variant="warning" className="hidden sm:inline-flex">
                  Setup Mode
                </Badge>
              )}
              <Button
                size="sm"
                onClick={() => setAddOpen(true)}
                disabled={accountPair.status === "closed"}
              >
                <Plus aria-hidden="true" className="mr-1 h-4 w-4" />
                Add Hedge
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label="Pair actions"
                  className="inline-flex h-7 w-7 cursor-default items-center justify-center rounded-[min(var(--radius-md),10px)] border border-input bg-transparent text-muted-foreground whitespace-nowrap transition-colors outline-none select-none hover:text-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-popup-open:border-accent/60 data-popup-open:text-accent dark:bg-input/30 dark:hover:bg-input/50"
                >
                  <MoreHorizontal aria-hidden="true" className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[min(18rem,calc(100vw-1.5rem))] p-1">
                  <DropdownMenuItem
                    disabled={busy || accountPair.status === "closed"}
                    onClick={async () => {
                      const ok = await confirm({
                        title: "Archive This Pair?",
                        description:
                          "Both accounts stay linked and you can reopen later.",
                        confirmText: "Archive",
                      });
                      if (!ok) return;
                      setBusy(true);
                      try {
                        await archiveAccountPair(accountPair.slug);
                        load();
                      } catch (e) {
                        toast.error(extractApiErrorMessage(e));
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        Archive Pair
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Park this pair. Reopen later — accounts stay linked.
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={busy || accountPair.status === "closed"}
                    onClick={() => {
                      void doRebuild();
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        Rebuild Pair…
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Re-resolve symbol mappings on both legs.
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={busy || accountPair.status === "closed"}
                    onClick={() => {
                      void doSwapLeg("long");
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        Replace {accountPair.longAccount.firmName}…
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Swap the long leg for another active account.
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={busy || accountPair.status === "closed"}
                    onClick={() => {
                      void doSwapLeg("short");
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        Replace {accountPair.shortAccount.firmName}…
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Swap the short leg for another active account.
                      </span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={busy}
                    onClick={() => {
                      void doDissolve();
                    }}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium">
                        Dissolve Pair…
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        Permanently break the link. Cannot be undone.
                      </span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {isArchived && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-500">
          <span className="font-medium">
            This Pair Is Archived. Reopen To Resume Trading.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void doReopen()}
            disabled={busy}
          >
            Reopen Pair
          </Button>
        </div>
      )}

      {!isArchived && isPaused && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-500">
          <span className="font-medium">
            This Pair Starts Paused For Setup. Review The Allowlist And Hedges, Then Complete Setup To Start Trading.
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void doCompleteSetup()}
            disabled={busy}
          >
            Complete Setup
          </Button>
        </div>
      )}

      {/* One hidden subscription per symbol pair — React rules-of-hooks
          forbid calling useHedgeSocket in a loop from this body, so each
          PairSubscriber calls the hook for its pair id and renders null. */}
      {symbolPairs.map((sp) => (
        <PairSubscriber key={sp.id} pairId={sp.id} />
      ))}

      <LegsHeader pair={accountPair} />

      {symbolPairs.length > 0 && (
        <LegsLive accountPair={accountPair} symbolPairs={symbolPairs} />
      )}

      <AllowlistBlock
        slug={accountPair.slug}
        allowlist={accountPair.symbolAllowlist}
        preferredSymbols={PREFERRED_SYMBOLS}
        onChange={(next) =>
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  accountPair: { ...prev.accountPair, symbolAllowlist: next },
                }
              : prev,
          )
        }
      />

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-foreground">
          Symbol Hedges ({symbolPairs.length})
        </h2>
      </div>

      <ActiveHedges accountPair={accountPair} symbolPairs={symbolPairs} onChanged={load} />

      <AddHedgeSheet
        open={addOpen}
        onOpenChange={setAddOpen}
        accountPairSlug={accountPair.slug}
        allowlist={accountPair.symbolAllowlist}
        defaultLots={defaultLots}
        onAllowlistChange={(next) =>
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  accountPair: { ...prev.accountPair, symbolAllowlist: next },
                }
              : prev,
          )
        }
        onHedgeCreated={load}
        longAccountLabel={`${accountPair.longAccount.firmName} · #${accountPair.longAccount.login ?? accountPair.longAccount.slug}`}
        shortAccountLabel={`${accountPair.shortAccount.firmName} · #${accountPair.shortAccount.login ?? accountPair.shortAccount.slug}`}
      />
    </div>
  );
}
